import {FlowLang} from 'flow-to-typescript-codemod';
import _ from 'lodash';
import {Record as ImmRecord, Map, List, Set, OrderedSet} from 'immutable';
import {
  getStylesFromStyleBlock,
  getOrderedList,
  getBlock,
  getTreePath,
  exportStyleBlocks,
} from '../utils/StyleBlockUtils';
import {DEFAULT_VALUES as DEFAULT_BREAKPOINT_RESOLUTIONS} from '@packages/systems/style-legacy/media-queries';
import {weakMemo} from '@packages/utilities/memo';
import {reportBug} from '@packages/utilities/reporter';

import type {BreakpointID} from '@packages/systems/style/types';
import type {
  StyleBlockId,
  StyleBlock,
} from '@packages/systems/style-legacy/records';

import {
  AffectedNodesByStyleBlockId,
  calculateAffectedForStyleBlock,
  recalculateAffected,
} from '@packages/shared/utils/AffectedNodesUtils';

import type {
  RawStyleBlockServerType as ServerStyleBlock,
  PseudoStateKey,
  CustomNodeStateKey,
} from '@packages/systems/core/utils/StyleTypes';

import {isStyleBlockUsedByMacro} from '@packages/shared/utils/IXStateUtils';
import {isStyleBlockUsedByIx2} from '@packages/systems/ix2/shared-utils-IX2Utils';
import {isStyleBlockUsedByIx3} from '@packages/systems/ix3/shared';
import type {IX3State} from '@packages/systems/ix3/store';

import type {
  ChangedRuleShape,
  DecoratableWithMaps,
  StyleBlockStateMethods,
  StyleBlockStateShape,
  StyleBlockStateType,
} from './StyleBlockStateTypes';
import {getEffectiveBreakpoints} from '@packages/systems/style/media-queries';
import {Component, Plugins} from '@packages/systems/wfdl';
import {
  VariantDataMap,
  VariantStateKey,
  emptyVariantDataMap,
} from '@packages/systems/style-legacy/selectors';
import {emptyObject} from '@packages/utilities/fp/utils';
import {
  LibrariesCapabilitiesMap,
  EMPTY_INSTALLED_LIBRARY_TAG_STYLES_SETTINGS,
  isHiddenLibraryTagStyle,
  sortStyleBlocksForCascade,
} from '@packages/systems/libraries';

// @ts-expect-error - TS7006 - Parameter 'globalOrder' implicitly has an 'any' type.
const getGlobalOrderIndex = weakMemo(function getGlobalOrderIndex(globalOrder) {
  let index = 0;
  // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS2314 - Generic type 'Record<TProps>' requires 1 type argument(s). | TS7006 - Parameter 'result' implicitly has an 'any' type. | TS7006 - Parameter 'guid' implicitly has an 'any' type.
  return globalOrder.reduce<Record<string, number>>((result, guid) => {
    result[guid] = index++;
    return result;
  }, {});
});

const getGlobalStyleBlocks = weakMemo(function getGlobalStyleBlocks(
  styleBlockState: StyleBlockState
): OrderedSet<StyleBlock> {
  // Return an OrderedSet by mutating a new OrderedSet.
  const blocks = OrderedSet<StyleBlock>().asMutable();
  styleBlockState.globalOrder.forEach((guid) => {
    const block = styleBlockState.getBlock(guid);
    if (block) blocks.add(block);
  });
  return blocks.asImmutable();
});

const ChangedRule = ImmRecord<ChangedRuleShape>({
  // The guid (e.g. "block-123:main_hover") of the StyleRule that was changed via updateStyleRule
  guid: null,

  // Indicates that the last CSS styleLess update should only be written to CSSOM but should not persist to the server
  ephemeral: false,

  // If a change is ephemeral, keeps the map of old CSS prop+values to restore to
  oldValues: Map(),
});

const Parent = ImmRecord<StyleBlockStateShape>({
  // @ts-expect-error - TS2740 - Type '{}' is missing the following properties from type 'BreakpointsMap<Breakpoint>': xxl, xl, large, main, and 3 more
  breakpoints: {},

  // A guid -> StyleBlockRecord map that is the primary source of truth for the styles that make up the site
  styleBlocks: Map(),

  // A guid -> Map(pageId => count) map which records how many times each StyleBlock is used on each page
  // We keep this info outside of styleBlocks to make it easier to detect when block changes require a CSSOM sync
  affectsMap: Map(),

  // A child_guid -> parent_guid to enable walking up the style tree
  parentIndex: Map(),

  // A list of guids representing the order of the root/non-nested blocks
  // Nested StyleBlocks are ordered by each record's children array
  globalOrder: OrderedSet(),

  // Contains info about the last already-existing single StyleRule that had an atomic CSS change
  // This is used to instruct StyleSheetUtils.syncStyleSheet to take a shortcut and not re-compare the entire st
  changedRule: ChangedRule(),

  // The guid (e.g. 'block-123') that was just created, to signal to CSSOM sand others to avoid certain unnecessary costly operations
  _hack_addedRuleGuid: null,

  // A simple key -> Boolean map to keep track of which migrations have been run on the current site's style blocks
  // This is mostly used to pass through values from server-side JSON data back to CssSync
  migrations: Map(),

  // A guid -> Map(cssProperty => Boolean) map to keep track of which CSS properties apply to a specific
  // style block id. Used for canvas related features, like calculating if the empty dotted decorator has to be applied
  cssPropertyMap: Map(),

  // A simple key -> Boolean map to keep track of which style blocks belong to the site or page branch
  // This changes when loading a page from a page branch and vice-versa
  styleBlockIds: Set(),

  // A simple key -> Boolean map to keep track of which styleBlocks belong to the site only
  // This changes when loading a page from a page branch and vice-versa
  // When loading a page in the main site, this will be empty; when loading a page branch, this will contain the IDs for main site style blocks
  baseStyleBlockIds: Set(),

  // Indicates which branch's data is loaded into the style store. If null, main site styles are loaded. Since store
  // updates are not atomic, this only indicates that the StyleStore has data from this branch loaded and is used to
  // ensure we are not executing logic cross-stores while branch data is still loading.
  branchId: null,

  // The site's tag styles settings for its installed libraries, joined with
  // their priority order.
  installedLibrariesTagStylesSettings:
    EMPTY_INSTALLED_LIBRARY_TAG_STYLES_SETTINGS,

  // A copy of the StyleBlockStateType when asset-hover previewing began, as an easy
  // restore point.
  //
  // Something like this could be helpful in replacing action-sequences like this:
  // - startSetStyle (punch in)
  // - setStyle(s) (set some ephemeral styles [font family, bg img, etc])
  // - endSetStyle (punch out, with or without "commit")
  //
  // Tagged this as __HACK__ mainly to highlight that it hasn't been generalized yet.
  __HACK__assetHoverRestorePoint: null,
});

const _getOrderedBreakpointIds = weakMemo(
  (breakpoints: StyleBlockStateShape['breakpoints']) =>
    Object.keys(breakpoints) as Array<keyof StyleBlockStateShape['breakpoints']>
);

// TODO: Move all these local accessors to StyleBlockUtils or similar
/**
 * See StyleBlockStateType
 */
class StyleBlockState extends Parent implements StyleBlockStateMethods {
  declare breakpoints: StyleBlockStateShape['breakpoints'];
  declare styleBlocks: StyleBlockStateShape['styleBlocks'];
  declare affectsMap: StyleBlockStateShape['affectsMap'];
  declare parentIndex: StyleBlockStateShape['parentIndex'];
  declare globalOrder: StyleBlockStateShape['globalOrder'];
  declare changedRule: StyleBlockStateShape['changedRule'];
  declare _hack_addedRuleGuid: StyleBlockStateShape['_hack_addedRuleGuid'];
  declare migrations: StyleBlockStateShape['migrations'];
  declare cssPropertyMap: StyleBlockStateShape['cssPropertyMap'];
  declare styleBlockIds: StyleBlockStateShape['styleBlockIds'];
  declare baseStyleBlockIds: StyleBlockStateShape['baseStyleBlockIds'];
  declare branchId: StyleBlockStateShape['branchId'];
  declare __HACK__assetHoverRestorePoint: StyleBlockStateShape['__HACK__assetHoverRestorePoint'];

  // @ts-expect-error - TS2425 - Class 'Record<StyleBlockStateShape> & Readonly<StyleBlockStateShape>' defines instance member property 'resetChangedRule', but extended class 'StyleBlockState' defines it as instance member function.
  resetChangedRule(): StyleBlockState {
    return this.update(
      'changedRule',
      (changedRule: StyleBlockStateShape['changedRule']) => {
        return changedRule
          .set('guid', null)
          .set('ephemeral', false)
          .update('oldValues', (oldValues) => {
            return oldValues.clear();
          });
      }
    );
  }

  // @ts-expect-error - TS2425 - Class 'Record<StyleBlockStateShape> & Readonly<StyleBlockStateShape>' defines instance member property 'resetAddedRule', but extended class 'StyleBlockState' defines it as instance member function.
  resetAddedRule(): StyleBlockState {
    return this.set('_hack_addedRuleGuid', null);
  }

  /*
   * Get all breakpoint IDs which include an overlapping range with the current one.
   * Result includes the current ID.
   */
  getEffectiveBreakpointIds(currentBreakpointId: BreakpointID) {
    return getEffectiveBreakpoints(this.breakpoints, {
      width: DEFAULT_BREAKPOINT_RESOLUTIONS[currentBreakpointId],
    }).map((breakpoint) => breakpoint.id);
  }

  getOrderedBreakpointIds() {
    return _getOrderedBreakpointIds(this.breakpoints);
  }

  getBlock(
    guid?: string | null,
    params: DecoratableWithMaps & {
      skipVariants?: boolean;
    } = {}
  ): StyleBlock | undefined {
    return getBlock(this, guid, params);
  }

  getBlockIdClosure(rootStyleBlockId: string) {
    const result: Array<StyleBlock['_id']> = [];
    const rootStyleBlock = this.getBlock(rootStyleBlockId);

    // If the block is not found by ID, just return the original ID as the default
    if (!rootStyleBlock) {
      return [rootStyleBlockId];
    }

    // @ts-expect-error - TS7006 - Parameter 'styleBlock' implicitly has an 'any' type.
    this.walkStyleBlockTree([rootStyleBlock], (styleBlock) => {
      const styleBlockId = styleBlock.get('_id');
      result.push(styleBlockId);
    });

    return result;
  }

  getStyles({
    styleBlockGuid,
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
  }: {
    styleBlockGuid: StyleBlockId;
    mediaQuery?: BreakpointID;
    customNodeState?: null | CustomNodeStateKey;
    pseudoState?: null | PseudoStateKey;
    variantState?: null | VariantStateKey;
  }): null | string {
    if (!styleBlockGuid) {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug(
        'MissingStyleBlock',
        'Encountered missing style block reference in getStyles()',
        {
          styleBlockGuid,
          mediaQuery,
          customNodeState,
          pseudoState,
          team: 'platform',
        }
      );
      return null;
    }

    const styleBlock = this.getBlock(styleBlockGuid);
    if (!styleBlock) {
      return null;
    }

    return getStylesFromStyleBlock({
      styleBlock,
      mediaQuery,
      customNodeState,
      pseudoState,
      variantState,
    });
  }

  getParent(childGuid: string): StyleBlock | null | undefined {
    return this.getBlock(this.parentIndex.get(childGuid));
  }

  getChildren(
    guid: string,
    params: DecoratableWithMaps = {}
  ): OrderedSet<StyleBlock> {
    const block = this.getBlock(guid);
    if (!block) {
      return OrderedSet();
    }

    const children = block.get('children');

    if (children.size === 0) return OrderedSet();

    if (children.size === 1) {
      const childBlock = this.getBlock(children.first(), params);
      return childBlock ? OrderedSet([childBlock]) : OrderedSet();
    }

    return children
      .reduce((result, childGuid) => {
        const childBlock = this.getBlock(childGuid, params);
        if (childBlock) {
          result.add(childBlock);
        }
        return result;
      }, OrderedSet<StyleBlock>().asMutable())
      .asImmutable();
  }

  getTreePath(leafGuid: string): Array<StyleBlock> {
    const leafBlock = this.getBlock(leafGuid);

    if (!leafBlock) {
      return [];
    }

    return getTreePath(leafBlock, this.parentIndex, this.styleBlocks);
  }

  // @ts-expect-error - TS2425 - Class 'Record<StyleBlockStateShape> & Readonly<StyleBlockStateShape>' defines instance member property 'getRoot', but extended class 'StyleBlockState' defines it as instance member function.
  getRoot(leafGuid?: string | null) {
    if (!leafGuid) {
      return null;
    }
    return this.getTreePath(leafGuid)[0];
  }

  getGlobalStyleBlocks(): OrderedSet<StyleBlock> {
    return getGlobalStyleBlocks(this);
  }

  getGlobalClassBlocksMap(): Map<string, StyleBlock> {
    // @ts-expect-error - TS2322 - Type 'Map<unknown, unknown>' is not assignable to type 'Map<string, StyleBlock>'.
    return this.globalOrder
      .reduce((result, guid) => {
        const block = this.getBlock(guid);

        if (block && block.get('type') === 'class') {
          result.set(block.get('_id'), block);
        }

        return result;
      }, Map().asMutable())
      .asImmutable();
  }

  getOrderedList({
    slugMap,
    variantDataMap,
    ...params
  }: DecoratableWithMaps & {
    rootGuid?: string;
    includeFake?: boolean;
  }): Array<StyleBlock> {
    return sortStyleBlocksForCascade(this.installedLibrariesTagStylesSettings)(
      getOrderedList(this, {
        slugMap: slugMap ?? emptyObject,
        variantDataMap: variantDataMap ?? emptyVariantDataMap,
        ...params,
      })
    );
  }

  getPartialOrderedList({
    leafGuids = [],
    includeFake = false,
  }: {
    leafGuids?: Array<string>;
    includeFake?: boolean;
  } = {}) {
    const result = [];
    const uniqSet = new window.Set();
    const length = leafGuids.length;

    for (let i = 0; i < length; i++) {
      const guid = leafGuids[i];
      if (!guid || uniqSet.has(guid)) continue;
      if (!_.isString(guid)) throw new Error('Invalid StyleBlock ID');

      const block = this.getRoot(guid);

      if (block && !uniqSet.has(block.get('_id'))) {
        uniqSet.add(block.get('_id'));
        result.push(block);
      }
    }

    // Build a <guid>:<index> map to make sorting possible
    const globalOrderIndexMap = getGlobalOrderIndex(this.globalOrder);

    // Sort just the global root blocks based on the globalOrder
    result.sort(
      (block1, block2) =>
        globalOrderIndexMap[block1.get('_id')] -
        globalOrderIndexMap[block2.get('_id')]
    );

    const rootBlocksWithDescendants: Array<StyleBlock> = [];

    // Merge in all descendants, their nested sorting is dependent on inner ordered .children() lists
    for (const rootBlock of result) {
      Array.prototype.push.apply(
        rootBlocksWithDescendants,
        getOrderedList(this, {
          // NOTE: Since we are explicitly _not_ decorating our styleblocks we
          // can pass an empty slugMap/variantDataMap here:
          slugMap: {},
          variantDataMap: emptyVariantDataMap,
          rootGuid: rootBlock.get('_id'),
          includeFake,
          decorate: false,
        })
      );
    }

    return sortStyleBlocksForCascade(this.installedLibrariesTagStylesSettings)(
      rootBlocksWithDescendants
    );
  }

  isHiddenLibraryTagStyle(blockGuid: string): boolean {
    const block = this.styleBlocks.get(blockGuid);

    return (
      block != null &&
      isHiddenLibraryTagStyle(block, this.installedLibrariesTagStylesSettings)
    );
  }

  getClassNameMap() {
    const classNameMap: Record<string, any> = {};

    this.styleBlocks.forEach((block) => {
      classNameMap[block.get('_id')] = block.get('name');
    });

    return classNameMap;
  }

  isInEphemeralState(): boolean {
    return this.changedRule.get('ephemeral') === true;
  }

  walkStyleBlockTree(
    styleBlocks: List<StyleBlock> | Array<StyleBlock>,
    callback: any
  ) {
    // @ts-expect-error - TS7006 - Parameter 'styleBlock' implicitly has an 'any' type.
    const recurse = (styleBlock) => {
      callback(styleBlock);

      const styleBlockChildren = styleBlock && styleBlock.get('children');

      styleBlockChildren &&
        // @ts-expect-error - TS7006 - Parameter 'childStyleBlockGuid' implicitly has an 'any' type.
        styleBlockChildren.forEach((childStyleBlockGuid) => {
          recurse(this.getIn(['styleBlocks', childStyleBlockGuid]));
        });
    };

    styleBlocks.forEach(recurse);
  }

  isRemovableStyleBlock(
    styleBlock: StyleBlock | null | undefined,
    componentMap: Component.ComponentMap,
    plugins: Plugins.PluginsType,
    currentPageId: string,
    ixState: Record<any, any>,
    ix2State: Record<any, any>,
    ix3State: IX3State,
    librariesCapabilitiesMap: LibrariesCapabilitiesMap
  ): boolean {
    if (!styleBlock) {
      return false;
    }

    const containsAffectedNodes = (styleBlockId: StyleBlockId): boolean => {
      const affectedIds = calculateAffectedForStyleBlock({
        componentMap,
        plugins,
        // @ts-expect-error - TS2322 - Type 'this' is not assignable to type 'StyleBlockStateType'.
        styleBlockState: this,
        styleBlockId,
        librariesCapabilitiesMap,
      });

      return affectedIds.length > 0;
    };

    // @ts-expect-error - TS7006 - Parameter 'sb' implicitly has an 'any' type.
    const isStyleBlockUsed = (sb) => {
      if (!sb) {
        return false;
      }
      const styleBlockId = sb.get('_id');

      return (
        // First, check if the counts are non-zero for any other pages:
        // @ts-expect-error - TS2571 - Object is of type 'unknown'. | TS2554 - Expected 1 arguments, but got 2.
        this.getIn(['affectsMap', styleBlockId], Map()).some(
          // @ts-expect-error - TS7006 - Parameter 'count' implicitly has an 'any' type. | TS7006 - Parameter 'id' implicitly has an 'any' type.
          (count, id) => id !== currentPageId && count > 0
        ) ||
        isStyleBlockUsedByMacro(ixState, styleBlock) ||
        isStyleBlockUsedByIx2(ix2State)(styleBlockId) ||
        isStyleBlockUsedByIx3(ix3State)(styleBlockId) || // If no other pages contain the styleblock, calculate the counts for
        // the current page on the fly to ensure they're up to date:
        containsAffectedNodes(styleBlockId)
      );
    };

    // Then make sure that the style block is not used
    if (isStyleBlockUsed(styleBlock)) {
      return false;
    }

    if (
      styleBlock.get('type') === 'class' ||
      styleBlock.get('type') === 'element'
    ) {
      // Regular class style block
      // Recursively check that all children of this style block are orphans as well, or else this one isn't
      // This is to handle a legacy case where a combo styleBlock is atached to a node but none of its parents are
      return styleBlock.get('children').every((child) => {
        // If it's an unrolled styleBlock this lookup will have already been done
        if (typeof child === 'string') {
          // @ts-expect-error - TS2322 - Type 'StyleBlock | undefined' is not assignable to type 'string | undefined'.
          child = this.getBlock(child);
        }

        if (child) {
          return this.isRemovableStyleBlock(
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'StyleBlock'.
            child,
            componentMap,
            plugins,
            currentPageId,
            ixState,
            ix2State,
            ix3State,
            librariesCapabilitiesMap
          );
        }

        return false;
      });
    } else if (
      styleBlock.get('type') === 'tag' &&
      Boolean(styleBlock.get('comb'))
    ) {
      // Nested tag style block
      // Nested tag style blocks are orphans if and only if the parent is unused
      const parentStyleBlock = this.getParent(styleBlock.get('_id'));
      const parentIsUsed =
        Boolean(parentStyleBlock) && isStyleBlockUsed(parentStyleBlock);
      return !parentIsUsed;
    } else {
      return false;
    }
  }

  decorateStyleBlockChildren(styleBlock: StyleBlock): StyleBlock {
    return styleBlock.update(
      'children',
      (childrenGuids: StyleBlock['children']) => {
        return childrenGuids.map((childGuid) => {
          const childBlock = this.getBlock(childGuid);
          return childBlock
            ? this.decorateStyleBlockChildren(childBlock)
            : childBlock;
        });
      }
    );
  }

  exportStyleBlocks({
    componentMap,
    plugins,
    currentPageId,
    slugMap,
    variantDataMap,
    exportAllPageStyles,
    hashContentSelectorsForPublishingCss,
  }: {
    componentMap: Component.ComponentMap;
    plugins: Plugins.PluginsType;
    currentPageId: string;
    slugMap: Plugins.PluginsType['slugMap'];
    variantDataMap: VariantDataMap;
    /**
     * If true, this will export all style blocks associated with the page, including those that were not created on this branch.
     */
    exportAllPageStyles?: boolean;
    hashContentSelectorsForPublishingCss?: boolean;
  }): Array<ServerStyleBlock> {
    const styleMap: AffectedNodesByStyleBlockId = recalculateAffected({
      componentMap,
      plugins,
      styleBlocks: this.styleBlocks,
      slugMap,
    });

    return exportStyleBlocks({
      styleBlockData: this,
      affectedNodesMap: styleMap,
      currentPageId,
      slugMap,
      variantDataMap,
      exportAllPageStyles,
      hashContentSelectorsForPublishingCss,
    });
  }
}

export default StyleBlockState as FlowLang.Class<StyleBlockStateType>;
